
import { Modal, message, Spin, Row, Col, Form, Empty } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setId } from "../../../Slices/employeeslice";
import { useMutation, useQuery } from "@apollo/client";
import { RESET_TOKEN } from "../../../Queries/ResetToken";
import { GET_EMPLOYEE_DETAILS } from "../../../Queries/Employees";
import { InputField, AntdButton } from "../../../blueplanitUtils/InputField";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable"


const ResetToken = () => {
    const dispatch = useDispatch();

    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const { data, refetch, loading } = useQuery(GET_EMPLOYEE_DETAILS);
    const employees = data?.EmployeeDetail?.response;

    const [resetTokenMutation] = useMutation(RESET_TOKEN);
    const DEPARTMENT_ORDER = [
        "IT",
        "Sales",
        "Operations",
        "Other",
        "HR & Admin",
        "Both",
    ];
    const [paginationByDept, setPaginationByDept] = useState({});



    useEffect(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        dispatch(setId(true));
        setFilteredEmployees(employees);
    }, [employees, dispatch]);

    const getPaginationConfig = (dept) => {
        return {
            current: paginationByDept[dept]?.current || 1,
            pageSize: paginationByDept[dept]?.pageSize || 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) => {
                setPaginationByDept((prev) => ({
                    ...prev,
                    [dept]: { current: page, pageSize },
                }));
            },
            onShowSizeChange: (page, pageSize) => {
                setPaginationByDept((prev) => ({
                    ...prev,
                    [dept]: { current: 1, pageSize },
                }));
            },
        };
    };


    const handleChange = (e) => {
        const val = e.target.value.toLowerCase();
        if (!val) {
            setFilteredEmployees(employees);
            return;
        }

        const filteredData = employees.filter(
            (item) =>
                item.firstname?.toLowerCase()?.includes(val) ||
                item.lastname?.toLowerCase()?.includes(val)
        );
        setFilteredEmployees(filteredData);
    };

    const showResetTokenModal = (employee) => {
        setSelectedEmployee(employee);
        setIsModalVisible(true);
    };

    const handleResetToken = async () => {
        if (!selectedEmployee) {
            message.error("Invalid Employee ID. Please try again.");
            return;
        }

        try {
            const { data } = await resetTokenMutation({
                variables: {
                    email: selectedEmployee.email,
                },
            });

            if (data.ResetToken.success === true) {
                message.success("Token Reset Successful, Email sent");
                refetch();
            } else {
                message.error("Failed to reset token.");
            }
        } catch (error) {
            message.error("Failed to reset token. Please try again.");
        }

        setIsModalVisible(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const columns = [
        {
            title: "User Name",
            dataIndex: "UserName",
            render: (_, record) => {
                return `${record.firstname || ""} ${record.lastname || ""}`;
            },
            width: 100,
            align: "start",
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#e0e0e0",
                    color: "black",
                },
            }),
        },
        {
            title: "Email",
            dataIndex: "email",
            width: 200,
            align: "left",
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#e0e0e0",
                    color: "black",
                },
            }),
        },
        {
            title: "Token",
            dataIndex: "token",
            width: 100,
            align: "left",
            render: (_, record) => {
                return record.token || "N/A";
            },
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#e0e0e0",
                    color: "black",
                },
            }),
        },
        {
            title: "Action",
            dataIndex: "action",
            width: 100,
            align: "left",
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#e0e0e0",
                    color: "black",
                },
            }),
            render: (_, record) => (
                <AntdButton
                    label="Reset Token"
                    color="primary"
                    variant="solid"
                    htmlType="submit"
                    style={{ color: "white" }}
                    onClick={() => showResetTokenModal(record)}
                />
            ),
        },
    ];

    const employeeRows = filteredEmployees?.map((emp) => ({
        ...emp,
        key: emp?.email || emp?.id || `${emp?.firstname}-${emp?.lastname}`,
    }));

    const groupedEmployees = DEPARTMENT_ORDER.reduce((acc, dept) => {
        acc[dept] =
            filteredEmployees?.filter(
                (emp) =>
                    (emp.department || "Other").toLowerCase() === dept.toLowerCase()
            ) || [];
        return acc;
    }, {});


    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <Row>
                    <Col span={4}>
                        <Form.Item>
                            <InputField
                                type="text"
                                placeholder="Search By Name..."
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {loading ? (
                    <Spin size="large" style={{ display: "block", margin: "20px auto" }} />
                ) : !filteredEmployees?.length ? (
                    <Empty
                        description={
                            <span style={{ color: "#999" }}>
                                No Records Found. Try a different search.
                            </span>
                        }
                        style={{ padding: "60px 0" }}
                    />
                ) : (
                    DEPARTMENT_ORDER.map((dept) => {
                        const deptEmployees = groupedEmployees[dept];

                        if (!deptEmployees || deptEmployees.length === 0) return null;

                        return (
                            <div key={dept} style={{ marginBottom: "32px" }}>
                                <h3 style={{ marginBottom: "16px", fontWeight: 600 }}>
                                    {dept}
                                </h3>

                                <CustomGridTable
                                    columns={columns}
                                    Id={`reset-token-${dept}-table`}
                                    rowKey="key"
                                    dataSource={deptEmployees.map((emp) => ({
                                        ...emp,
                                        key: emp?.email || emp?.id,
                                    }))}
                                    pagination={getPaginationConfig(dept)}
                                />

                            </div>
                        );
                    })
                )}


                <Modal
                    open={isModalVisible}
                    onOk={handleResetToken}
                    onCancel={handleCancel}
                    style={{ textAlign: "center" }}
                    footer={[
                        <div
                            key="footer"
                            style={{ display: "flex", justifyContent: "center", gap: "20px" }}
                        >
                            <AntdButton key="cancel" onClick={handleCancel} label="No" />
                            <AntdButton key="ok" type="primary" onClick={handleResetToken} label="Yes" />
                        </div>,
                    ]}
                >
                    Are you sure you want to reset the token for{" "}
                    <b>{selectedEmployee?.firstname} {selectedEmployee?.lastname}</b>?
                </Modal>
            </div>
        </div>
    );
};


export default ResetToken;
